import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ClipboardCheck, Search, Plus, Eye, RefreshCw, X } from 'lucide-react';
import api from '../../api';
import { toast } from 'sonner';

const SupervisoryNotesPage = () => {
  const [notes, setNotes] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    patientId: '', patientName: '', visitType: 'RN Supervision',
    visitDate: new Date().toISOString().split('T')[0], supervisorName: '',
    aidePresent: true, findings: '', recommendations: '', status: 'Draft'
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [docsRes, patientsRes] = await Promise.all([
        api.get('/visit-documents/type/Supervisory'),
        api.get('/patients')
      ]);
      if (docsRes.data.success) setNotes(docsRes.data.documents || []);
      if (patientsRes.data.success) setPatients(patientsRes.data.patients || []);
    } catch (error) {
      toast.error('Failed to load supervisory notes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.patientName) { toast.error('Please select a patient'); return; }
    try {
      const res = await api.post('/visit-documents', {
        ...formData,
        discipline: 'Supervisory',
        documentType: formData.visitType
      });
      if (res.data.success) {
        toast.success('Supervisory note created');
        setNotes(prev => [...prev, res.data.document]);
        setShowModal(false);
        setFormData({
          patientId: '', patientName: '', visitType: 'RN Supervision',
          visitDate: new Date().toISOString().split('T')[0], supervisorName: '',
          aidePresent: true, findings: '', recommendations: '', status: 'Draft'
        });
      }
    } catch (error) {
      toast.error('Failed to create note');
    }
  };

  const filteredNotes = notes.filter(n =>
    n.patientName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    n.supervisorName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const stats = {
    total: notes.length,
    thisMonth: notes.filter(n => {
      const noteDate = new Date(n.visitDate);
      const now = new Date();
      return noteDate.getMonth() === now.getMonth() && noteDate.getFullYear() === now.getFullYear();
    }).length,
    completed: notes.filter(n => n.status === 'Completed').length
  };

  return (
    <DashboardLayout>
      <div className="p-6" data-testid="supervisory-notes-page">
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <ClipboardCheck className="w-6 h-6 text-indigo-600" />
                Supervisory Visit Notes
              </h1>
              <p className="text-gray-500 text-sm mt-1">Document RN supervisory visits for home health aides</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={fetchData} variant="outline"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setShowModal(true)}>
                <Plus className="w-4 h-4 mr-2" />New Supervisory Note
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-indigo-700">{stats.total}</div>
            <div className="text-sm text-indigo-600">Total Notes</div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-700">{stats.thisMonth}</div>
            <div className="text-sm text-blue-600">This Month</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-700">{stats.completed}</div>
            <div className="text-sm text-green-600">Completed</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Search by patient or supervisor..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-32"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : filteredNotes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No supervisory notes found. Click "New Supervisory Note" to create one.</div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Patient</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Visit Type</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Supervisor</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredNotes.map((note) => (
                  <tr key={note.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">{note.patientName}</td>
                    <td className="px-6 py-4">{note.documentType || note.visitType}</td>
                    <td className="px-6 py-4">{note.visitDate}</td>
                    <td className="px-6 py-4">{note.supervisorName || '-'}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${note.status === 'Completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {note.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <Button size="sm" variant="outline"><Eye className="w-4 h-4 mr-1" />View</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">New Supervisory Note</h2>
                <button onClick={() => setShowModal(false)}><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Patient *</label>
                  <select value={formData.patientId} onChange={(e) => {
                    const p = patients.find(p => p.id === e.target.value);
                    setFormData(prev => ({ ...prev, patientId: e.target.value, patientName: p ? `${p.firstName} ${p.lastName}` : '' }));
                  }} className="w-full border rounded-md px-3 py-2">
                    <option value="">Select Patient</option>
                    {patients.map(p => <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Visit Type</label>
                    <select value={formData.visitType} onChange={(e) => setFormData(prev => ({ ...prev, visitType: e.target.value }))} className="w-full border rounded-md px-3 py-2">
                      <option>RN Supervision</option>
                      <option>LPN Supervision</option>
                      <option>HHA Competency</option>
                      <option>Joint Visit</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Visit Date</label>
                    <Input type="date" value={formData.visitDate} onChange={(e) => setFormData(prev => ({ ...prev, visitDate: e.target.value }))} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Supervisor Name</label>
                  <Input value={formData.supervisorName} onChange={(e) => setFormData(prev => ({ ...prev, supervisorName: e.target.value }))} placeholder="e.g., Jane Smith, RN" />
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={formData.aidePresent} onChange={(e) => setFormData(prev => ({ ...prev, aidePresent: e.target.checked }))} />
                  <label className="text-sm">Aide present during visit</label>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Findings</label>
                  <textarea value={formData.findings} onChange={(e) => setFormData(prev => ({ ...prev, findings: e.target.value }))} className="w-full border rounded-md px-3 py-2" rows={2} placeholder="Document observations..." />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Recommendations</label>
                  <textarea value={formData.recommendations} onChange={(e) => setFormData(prev => ({ ...prev, recommendations: e.target.value }))} className="w-full border rounded-md px-3 py-2" rows={2} placeholder="Any recommendations..." />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={handleSubmit}>Create</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SupervisoryNotesPage;
